import {
  and,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { BaseRecord } from "../user/BaseApi";
import { db } from "../../firebase/firebase";
import { DEFAULT_DS_QUY } from "../../constants/config";
import { snapshotToArray } from "../../utils/fbUtil";
import { getErrorMessage } from "../user/BaseApi";

export type BtcGrpRecord = BaseRecord & {
  group: string;
  email: string;
  note: string;
  deleted: boolean;
};

export type BtcDkRecord = BaseRecord & {
  ngay: string;
  gio: string;
  email: string;
  dsQuy: any[];
  bill: string;
  note: string;
  xacNhan: boolean;
  flag: number;
  deleted: boolean;
};

export type BtcTvRecord = BaseRecord & {
  email: string;
  role: string;
  tp: string;
  ms?: string;
  note?: string;
};

export type BtcQuyRecord = BaseRecord & {
  dsQuy: any[];
  note: string;
  defaultDsQuy?: string;
};

type BtcDkLogRecord = BtcDkRecord;

/**
 * CURD
 */

class BtcGrpApi {
  private readonly collectionName = "btc";

  async create({
    group,
    email,
    note = "",
    createdAtMs,
  }: {
    group: string;
    email: string;
    note?: string;
    createdAtMs?: number;
  }) {
    try {
      let createdAt: Timestamp;
      try {
        createdAt = createdAtMs
          ? Timestamp.fromMillis(createdAtMs)
          : Timestamp.now();
      } catch (ex: unknown) {
        console.log(getErrorMessage(ex));
        createdAt = Timestamp.now();
      }

      const grpObj: BtcGrpRecord = {
        id: group,
        group,
        email,
        note,
        createdAt,
        deleted: false,
      };
      const grpRef = doc(db, this.collectionName, group);
      await setDoc(grpRef, grpObj);

      const tvObj = { email, role: "admin", tp: "A", ms: "", note: "" };
      const tvRef = doc(db, this.collectionName, group, "tv", email);
      await setDoc(tvRef, tvObj);

      const quyRef = doc(db, this.collectionName, group, "quy", "default");
      await setDoc(quyRef, { dsQuy: JSON.stringify(DEFAULT_DS_QUY), note: "" });

      return {
        result: {
          id: group,
          group,
          email,
          note,
          createdAtMs: createdAt.toMillis(),
        },
      };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async update(id: string, changes: Partial<BtcGrpRecord>) {
    try {
      const grpRef = doc(db, this.collectionName, id);
      if (changes.email && typeof changes.email === "string") {
        const tvRef = doc(db, this.collectionName, id, "tv", changes.email);
        const tvObj = {
          email: changes.email,
          role: "admin",
          tp: "A",
          ms: "",
          note: "",
        };
        await setDoc(tvRef, tvObj);
      }
      await updateDoc(grpRef, changes);
      return { result: { id, ...changes } };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async getAll() {
    try {
      const snapshot = await getDocs(collection(db, this.collectionName));
      const result = snapshotToArray(snapshot) as BtcGrpRecord[];
      result.forEach((obj) => {
        if (obj.createdAt) {
          obj.createdAtMs = obj.createdAt.toMillis();
          delete obj.createdAt;
        }
      });
      return { result };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }
}
const btcGrpApi = new BtcGrpApi();
export const btcGrpGetAll = () => btcGrpApi.getAll();
export const btcGrpCreate = (params: {
  group: string;
  email: string;
  note?: string;
  createdAtMs?: number;
}) => btcGrpApi.create(params);
export const btcGrpUpdate = (id: string, changes: Partial<BtcGrpRecord>) =>
  btcGrpApi.update(id, changes);

class BtcDkApi {
  private readonly collectionBase = "btc";

  async getAllBtcDk({
    email,
    start,
    end,
    grp,
  }: {
    email?: string;
    start: string;
    end: string;
    grp: string;
  }) {
    try {
      const ngayCnd = and(where("ngay", ">=", start), where("ngay", "<=", end));
      const cnds = email ? and(where("email", "==", email), ngayCnd) : ngayCnd;
      const q = query(collection(db, `${this.collectionBase}/${grp}/dk`), cnds);
      const snapshot = await getDocs(q);
      const result = snapshotToArray(snapshot) as BtcDkRecord[];
      return { result };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async create(
    btcDk: Partial<BtcDkRecord> & { id?: string | null },
    grp: string,
  ) {
    try {
      const collectionRef = collection(db, `${this.collectionBase}/${grp}/dk`);
      const docRef = btcDk.id
        ? doc(db, `${this.collectionBase}/${grp}/dk`, btcDk.id)
        : doc(collectionRef);
      const payload = { ...btcDk, id: docRef.id } as BtcDkRecord;
      await setDoc(docRef, payload);
      return { result: payload };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async update(id: string, changes: Partial<BtcDkRecord>, grp: string) {
    try {
      const ref = doc(db, `${this.collectionBase}/${grp}/dk`, id);
      await updateDoc(ref, changes);
      console.log( "update", { id, ...changes });
      return { result: { id, ...changes } };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async remove(id: string, grp: string) {
    try {
      const ref = doc(db, `${this.collectionBase}/${grp}/dk`, id);
      await deleteDoc(ref);
      return { result: { id } };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async findDk(ngay: string, email: string, grp: string) {
    try {
      const q = query(
        collection(db, `${this.collectionBase}/${grp}/dk`),
        where("ngay", "==", ngay),
        where("email", "==", email),
      );
      const snapshot = await getDocs(q);
      const result = snapshotToArray(snapshot) as BtcDkRecord[];
      return { result };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }
}
const btcDkApi = new BtcDkApi();

const originalRemove = btcDkApi.remove.bind(btcDkApi);
const originalCreate = btcDkApi.create.bind(btcDkApi);
const originalUpdate = btcDkApi.update.bind(btcDkApi);
const originalGetAllBtcDk = btcDkApi.getAllBtcDk.bind(btcDkApi);
const originalFindBtcDk = btcDkApi.findDk.bind(btcDkApi);

function btcDk2FdData(dsQuy: unknown[]) {
  return dsQuy.map((obj: any) => `${obj.msQuy},${obj.soLuong},${obj.donVi}`);
}
function btcDkFormFdData(fdDsQuy: string[]) {
  return fdDsQuy.map((txt) => {
    const arr = txt.split(",");
    return {
      msQuy: arr[0],
      soLuong: parseInt(arr[1], 10),
      donVi: arr[2],
    };
  });
}

function btcDkWrapMethod(
  originalMethod: (...args: any[]) => Promise<{ error?: any; result?: any }>,
  idx: number | null,
) {
  return async function (this: unknown, ...args: any[]) {
    if (idx !== null) {
      const oldObj = args[idx] as Record<string, unknown> | undefined;
      if (oldObj?.dsQuy) {
        oldObj.dsQuy = btcDk2FdData(oldObj.dsQuy as unknown[]);
      }
    }

    const { error, result } = await originalMethod.apply(this, args);

    if (Array.isArray(result)) {
      result.forEach((btcDk: any) => {
        if (btcDk?.dsQuy) {
          btcDk.dsQuy = btcDkFormFdData(btcDk.dsQuy);
        }
      });
    } else if (result?.dsQuy) {
      result.dsQuy = btcDkFormFdData(result.dsQuy);
    }
    return { error, result };
  };
}

btcDkApi.remove = btcDkWrapMethod(originalRemove, null) as any;
btcDkApi.create = btcDkWrapMethod(originalCreate, 0) as any;
btcDkApi.update = btcDkWrapMethod(originalUpdate, 1) as any;
btcDkApi.getAllBtcDk = btcDkWrapMethod(originalGetAllBtcDk, 0) as any;
btcDkApi.findDk = btcDkWrapMethod(originalFindBtcDk, 0) as any;

export const getAllBtcDk = ({
  email,
  token = "",
  start,
  end,
  grp,
}: {
  email?: string;
  token?: string;
  start: string;
  end: string;
  grp: string;
}) => btcDkApi.getAllBtcDk({ email, start, end, grp });
export const createBtcDk = (
  btcDk: Partial<BtcDkRecord> & { id?: string | null },
  grp: string,
  token?: string,
) => btcDkApi.create(btcDk, grp);
export const updateBtcDk = (
  id: string,
  changes: Partial<BtcDkRecord>,
  grp: string,
  token?: string,
) => btcDkApi.update(id, changes, grp);
export const removeBtcDk = (id: string, grp: string, token?: string) =>
  btcDkApi.remove(id, grp);
export const findBtcDk = (
  ngay: string,
  email: string,
  grp: string,
  token?: string,
) => btcDkApi.findDk(ngay, email, grp);

class BtcTvApi {
  private readonly collectionBase = "btc";

  async getAll(grp: string) {
    try {
      const snapshot = await getDocs(
        query(collection(db, `${this.collectionBase}/${grp}/tv`)),
      );
      const result = snapshotToArray(snapshot) as BtcTvRecord[];
      return { result };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async getOne(email: string, grp: string) {
    try {
      const ref = doc(db, `${this.collectionBase}/${grp}/tv`, email);
      const docSnap = await getDoc(ref);
      if (docSnap.exists()) {
        return { result: { ...docSnap.data(), id: docSnap.id } as BtcTvRecord };
      }
      return { error: "not found" };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async create(obj: BtcTvRecord, grp: string) {
    try {
      const id = obj.id ?? obj.email;
      const payload = { ...obj, id };
      const ref = doc(db, `${this.collectionBase}/${grp}/tv`, id);
      await setDoc(ref, payload);
      return { result: payload };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async update(id: string, changes: Partial<BtcTvRecord>, grp: string) {
    try {
      const ref = doc(db, `${this.collectionBase}/${grp}/tv`, id);
      await updateDoc(ref, changes);
      return { result: { id, ...changes } };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async remove(id: string, grp: string) {
    try {
      const ref = doc(db, `${this.collectionBase}/${grp}/tv`, id);
      await deleteDoc(ref);
      return { result: { id } };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }
}
const btcTvApi = new BtcTvApi();
export const getAllBtcTv = (grp: string) => btcTvApi.getAll(grp);
export const getBtcTv = (email: string, grp: string) =>
  btcTvApi.getOne(email, grp);
export const createBtcTv = (
  id: string,
  tv: Omit<BtcTvRecord, "id">,
  grp: string,
) => btcTvApi.create({ id, ...tv } as BtcTvRecord, grp);
export const deleteBtcTv = (id: string, grp: string) =>
  btcTvApi.remove(id, grp);
export const updateBtcTv = (
  id: string,
  changes: Partial<BtcTvRecord>,
  grp: string,
) => btcTvApi.update(id, changes, grp);

/**
 * CURD
 */

class BtcQuyApi {
  private readonly collectionBase = "btc";

  async getBtcQuy({ ngay, grp }: { ngay: string; grp: string }) {
    try {
      const ref = doc(db, `${this.collectionBase}/${grp}/quy`, ngay);
      const docSnap = await getDoc(ref);
      if (docSnap.exists()) {
        return {
          result: { ...docSnap.data(), id: docSnap.id } as BtcQuyRecord,
        };
      }
      return { error: "not found" };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async createBtcQuy({
    grp,
    ngay,
    dsQuy,
    ...other
  }: {
    grp: string;
    ngay: string;
    dsQuy: unknown[];
    [key: string]: unknown;
  }) {
    try {
      const payload = { id: ngay, dsQuy, ...other } as BtcQuyRecord;
      const ref = doc(db, `btc/${grp}/quy`, ngay);
      await setDoc(ref, payload);
      return { result: payload };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async updateBtcQuy({
    grp,
    ngay,
    dsQuy,
    ...other
  }: {
    grp: string;
    ngay: string;
    dsQuy: unknown[];
    [key: string]: unknown;
  }) {
    try {
      const ref = doc(db, `btc/${grp}/quy`, ngay);
      const changes = { dsQuy, ...other };
      await updateDoc(ref, changes);
      return { result: { id: ngay, ...changes } };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async removeBtcQuy({ ngay, grp }: { ngay: string; grp: string }) {
    try {
      const ref = doc(db, `btc/${grp}/quy`, ngay);
      await deleteDoc(ref);
      return { result: { id: ngay } };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }

  async getDefaultConfig(grp: string) {
    try {
      const ref = doc(db, `btc/${grp}`);
      const docSnap = await getDoc(ref);
      if (docSnap.exists()) {
        const obj = { ...docSnap.data(), id: docSnap.id } as Record<
          string,
          unknown
        >;
        if (obj.defaultDsQuy) {
          obj.dsQuy = JSON.parse(String(obj.defaultDsQuy));
        }
        return { result: obj };
      }
      return { error: "not found" };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }
}
const btcQuyApi = new BtcQuyApi();

function btcQuy2FdData(dsQuy: unknown[]) {
  return JSON.stringify(dsQuy);
}
function btcQuyFormFdData(fdDsQuy: string) {
  return JSON.parse(fdDsQuy);
}
function btcQuyWrapMethod(
  originalMethod: (...args: any[]) => Promise<{ error?: any; result?: any }>,
  idx: number,
) {
  return async function (this: unknown, ...args: any[]) {
    const obj = args[idx] as Record<string, unknown> | undefined;
    if (obj?.dsQuy !== undefined) {
      obj.dsQuy = btcQuy2FdData(obj.dsQuy as unknown[]);
    }
    const { error, result } = await originalMethod.apply(this, args);
    if (result?.dsQuy !== undefined) {
      result.dsQuy = btcQuyFormFdData(result.dsQuy);
    }
    return { error, result };
  };
}
btcQuyApi.getBtcQuy = btcQuyWrapMethod(btcQuyApi.getBtcQuy, 0) as any;
btcQuyApi.createBtcQuy = btcQuyWrapMethod(btcQuyApi.createBtcQuy, 0) as any;
btcQuyApi.updateBtcQuy = btcQuyWrapMethod(btcQuyApi.updateBtcQuy, 0) as any;
btcQuyApi.removeBtcQuy = btcQuyWrapMethod(btcQuyApi.removeBtcQuy, 0) as any;
export const getBtcQuy = ({ ngay, grp }: { ngay: string; grp: string }) =>
  btcQuyApi.getBtcQuy({ ngay, grp });
export const createBtcQuy = ({
  grp,
  ngay,
  dsQuy,
  ...other
}: {
  grp: string;
  ngay: string;
  dsQuy: unknown[];
  [key: string]: unknown;
}) => btcQuyApi.createBtcQuy({ grp, ngay, dsQuy, ...other });
export const updateBtcQuy = ({
  grp,
  ngay,
  dsQuy,
  ...other
}: {
  grp: string;
  ngay: string;
  dsQuy: unknown[];
  [key: string]: unknown;
}) => btcQuyApi.updateBtcQuy({ grp, ngay, dsQuy, ...other });
export const removeBtcQuy = ({ grp, ngay }: { grp: string; ngay: string }) =>
  btcQuyApi.removeBtcQuy({ grp, ngay });
export const getDefaultConfig = (grp: string) =>
  btcQuyApi.getDefaultConfig(grp);

class BtcDkLogApi {
  private readonly collectionBase = "btc";

  async getAllBtcDkLog({ itemId, grp }: { itemId: string; grp: string }) {
    try {
      const q = query(
        collection(db, `${this.collectionBase}/${grp}/dk_log`),
        where("itemId", "==", itemId),
      );
      const snapshot = await getDocs(q);
      const result = snapshotToArray(snapshot) as BtcDkLogRecord[];
      return { result };
    } catch (ex: unknown) {
      return { error: getErrorMessage(ex) };
    }
  }
}
var btcDkLogApi = new BtcDkLogApi();
export const getBtcDkLog = (params: { itemId: string; grp: string }) =>
  btcDkLogApi.getAllBtcDkLog(params);
